import React, { createContext, useState, useContext, useEffect } from "react";

// Create context
const LanguageContext = createContext();

// Custom hook
export const useLanguage = () => useContext(LanguageContext);

// Provider
export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState("en");
    const [fontDelta, setFontDelta] = useState(0);
    const [voiceEnabled, setVoiceEnabled] = useState(false);

    // Font configuration
    const STEP = 2;
    const MAX_FONT_DELTA = 4;   // Maximum increase (+4px)
    const MIN_FONT_DELTA = -4;  // Maximum decrease (-4px)

    const toggleVoice = () => {
        setVoiceEnabled(prev => {
            const newState = !prev;

            if (!newState) {
                // 🔥 remove all highlights when turning OFF
                document
                    .querySelectorAll(".voice-highlight")
                    .forEach(el => el.classList.remove("voice-highlight"));

                window.speechSynthesis.cancel();
            }

            return newState;
        });
    };
    const resetFont = () => {
        document
            .querySelectorAll(".voice-highlight")
            .forEach(el => el.classList.remove("voice-highlight"));
        setFontDelta(0);
        setVoiceEnabled(false);
        window.speechSynthesis.cancel();
    };

    useEffect(() => {
        if (!voiceEnabled) return;

        let lastText = "";
        let timeout;
        let lastElement = null;

        const handleMouseOver = (e) => {
            clearTimeout(timeout);

            const el = e.target;
            const text = el.innerText?.trim();

            if (!text || text.length < 3 || text === lastText) return;

            timeout = setTimeout(() => {
                lastText = text;

                // 🔥 remove previous highlight
                if (lastElement) {
                    lastElement.classList.remove("voice-highlight");
                }

                // 🔥 add highlight to current element
                el.classList.add("voice-highlight");
                lastElement = el;

                const utterance = new SpeechSynthesisUtterance(text);
                utterance.rate = 1;

                window.speechSynthesis.cancel();
                window.speechSynthesis.speak(utterance);
            }, 400);
        };

        document.body.addEventListener("mouseover", handleMouseOver);

        return () => {
            document.body.removeEventListener("mouseover", handleMouseOver);

            // cleanup highlight + speech
            if (lastElement) {
                lastElement.classList.remove("voice-highlight");
            }

            window.speechSynthesis.cancel();
        };
    }, [voiceEnabled]);


    // Load saved values
    useEffect(() => {
        const savedLang = localStorage.getItem("appLanguage");
        const savedFont = localStorage.getItem("fontDelta");

        if (savedLang) setLanguage(savedLang);
        if (savedFont !== null) setFontDelta(Number(savedFont));
    }, []);

    // Apply font globally
    useEffect(() => {
        document.documentElement.style.setProperty(
            "--font-delta",
            `${fontDelta}px`
        );

        localStorage.setItem("fontDelta", fontDelta);
    }, [fontDelta]);

    const changeLanguage = (lang) => {
        setLanguage(lang);
        localStorage.setItem("appLanguage", lang);
    };

    // Increase font
    const increaseFont = () => {
        setFontDelta((prev) => Math.min(prev + STEP, MAX_FONT_DELTA));
    };

    // Decrease font
    const decreaseFont = () => {
        setFontDelta((prev) => Math.max(prev - STEP, MIN_FONT_DELTA));
    };

    return (
        <LanguageContext.Provider
            value={{
                language,
                changeLanguage,
                fontDelta,
                increaseFont,
                decreaseFont,
                resetFont,
                voiceEnabled,
                toggleVoice
            }}
        >
            {children}
        </LanguageContext.Provider>
    );
};